import React, { useState, useEffect } from 'react';
import {
  Menu, ChevronLeft, LogOut, Settings, Server, ChevronDown,
  MessageSquare, Users, Bot, Building2, Sparkles, FileText, Mail, StickyNote, Package, GitBranch, Facebook, BarChart3, Rocket, CalendarDays
} from 'lucide-react';
import Logo from './Logo';
import InfrastructureDashboard from './InfrastructureDashboard';
import Claw9Chat from './Claw9Chat';
import Claw9CRM from './Claw9CRM';
import Claw9Agents from './Claw9Agents';
import Claw9FaqSelfImprovement from './Claw9FaqSelfImprovement';
import MessengerSetupPanel from './MessengerSetupPanel';
import FacebookPostsDashboard from './FacebookPostsDashboard';
import { MyOffice } from './MyOffice';
import MyDocs from './MyDocs';
import GoogleIntegrations from './GoogleIntegrations';
import NotesPage from './NotesPage';
import InventoryPage from './InventoryPage';
import { DealFlowPage } from './DealFlow';
import Claw9Analytics from './Claw9Analytics';
import Claw9Onboarding from './Claw9Onboarding';
import OnboardingProgressBanner from './OnboardingProgressBanner';
import { getFeatureFlags } from '../utils/subscriptionHelpers';
import { useAppLanguage } from '../context/AppLanguageContext';

const DASHBOARD_TEXT = {
  en: {
    myOffice: 'My Office',
    onboarding: 'Setup Guide',
    agents: 'Agents',
    selfImprovement: 'Self-Improvement',
    chat: 'Chat',
    customers: 'Customers',
    myDocs: 'My Docs',
    notes: 'Notes',
    inventory: 'Inventory',
    dealFlow: 'Deal Flow',
    googleIntegration: 'Google',
    messagingChannels: 'Messaging Channels',
    facebookPosts: 'Facebook Posts',
    analytics: 'Analytics',
    infrastructure: 'Infrastructure',
    plan: 'Plan',
    settings: 'Settings',
    logout: 'Log out',
    featureLocked: 'Feature Locked',
    featureLockedDescription: 'This feature requires a higher subscription tier. Please upgrade your plan to access this feature.',
    english: 'English',
    vietnamese: 'Tiếng Việt',
  },
  vi: {
    myOffice: 'Văn phòng',
    onboarding: 'Hướng dẫn',
    agents: 'Agents',
    selfImprovement: 'Tự cải thiện',
    chat: 'Chat',
    customers: 'Khách hàng',
    myDocs: 'Tài liệu',
    notes: 'Ghi chú',
    inventory: 'Kho hàng',
    dealFlow: 'Kịch bản bán hàng',
    googleIntegration: 'Google',
    messagingChannels: 'Kênh nhắn tin',
    facebookPosts: 'Bài đăng Facebook',
    analytics: 'Thống kê',
    infrastructure: 'Hạ tầng',
    plan: 'Gói',
    settings: 'Cài đặt',
    logout: 'Đăng xuất',
    featureLocked: 'Tính năng bị khóa',
    featureLockedDescription: 'Tính năng này yêu cầu gói cao hơn. Vui lòng nâng cấp để sử dụng.',
    english: 'English',
    vietnamese: 'Tiếng Việt',
  },
};

const Dashboard = ({ user, onLogout }) => {
  const { language, setLanguage } = useAppLanguage();
  const t = DASHBOARD_TEXT[language] || DASHBOARD_TEXT.en;
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(() => {
    if (typeof window === 'undefined') return 'my-office';
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'my-office';
  });
  const [featureFlags, setFeatureFlags] = useState({});
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Get feature flags from user subscription
  useEffect(() => {
    const subscription = user?.subscription || user?.user?.subscription || 'basic';
    const flags = getFeatureFlags({ tier: subscription });
    setFeatureFlags(flags);
  }, [user]);

  // Navigation items - filtered by feature flags
  const navItems = [
    { id: 'my-office', label: t.myOffice, icon: Building2 },
    { id: 'onboarding', label: t.onboarding, icon: Rocket, isAction: true, highlight: true },
    { id: 'claw9-agents', label: t.agents, icon: Bot, requiresFeature: 'hasClaw9Agents' },
    { id: 'faq-self-improvement', label: t.selfImprovement, icon: Sparkles, requiresFeature: 'hasClaw9Agents' },
    { id: 'claw9-chat', label: t.chat, icon: MessageSquare, requiresFeature: 'hasClaw9Chat' },
    { id: 'claw9-crm', label: t.customers, icon: Users, requiresFeature: 'hasClaw9CRM' },
    { id: 'my-docs', label: t.myDocs, icon: FileText, requiresFeature: 'hasClaw9Chat' },
    { id: 'notes', label: t.notes, icon: StickyNote, requiresFeature: 'hasClaw9Chat' },
    { id: 'inventory', label: t.inventory, icon: Package, requiresFeature: 'hasClaw9Chat' },
    { id: 'deal-flow', label: t.dealFlow, icon: GitBranch, requiresFeature: 'hasClaw9Agents' },
    { id: 'google-integration', label: t.googleIntegration, icon: Mail, requiresEmail: 'andrew@claw9.ai' },
    { id: 'messaging-channels', label: t.messagingChannels, icon: MessageSquare, requiresEmail: 'andrew@claw9.ai' },
    { id: 'facebook-posts', label: t.facebookPosts, icon: Facebook, requiresFeature: 'hasClaw9Chat' },
    { id: 'analytics', label: t.analytics, icon: BarChart3, requiresFeature: 'hasClaw9Chat' },
    { id: 'infrastructure', label: t.infrastructure, icon: Server, requiresEmail: 'andrew@claw9.ai' },
  ];

  const userEmail = user?.email || user?.user?.email || '';

  // Filter nav items by feature access and email restrictions
  const availableNavItems = navItems.filter(item => {
    // Check email restriction first
    if (item.requiresEmail && userEmail !== item.requiresEmail) return false;
    // Check feature flag
    if (!item.requiresFeature) return true;
    return featureFlags[item.requiresFeature] === true;
  });

  const getUserEmail = () => {
    return user?.email || user?.user?.email || 'user@claw9.ai';
  };

  const getUserName = () => {
    return user?.name || user?.user?.name || getUserEmail().split('@')[0];
  };

  const getSubscriptionTier = () => {
    return featureFlags.tierName || 'Basic';
  };

  // Handle navigation (for cross-component navigation)
  const handleNavigate = (view, params) => {
    setCurrentTab(view);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', view);
      if (params?.channel) {
        url.searchParams.set('channel', params.channel);
      } else {
        url.searchParams.delete('channel');
      }
      window.history.replaceState({}, '', url.toString());
    }
  };

  return (
    <div className="min-h-screen bg-[#050810] flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 bg-[#0a0f1c] border-r border-white/10 flex flex-col transition-all duration-300 ${
          isSidebarOpen ? 'w-64' : 'w-16'
        }`}
      >
        {/* Logo */}
        <div className="p-4 flex items-center justify-between border-b border-white/10">
          {isSidebarOpen && <Logo size="small" />}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 rounded-lg hover:bg-white/5 text-white/60 hover:text-white transition-colors"
          >
            {isSidebarOpen ? <ChevronLeft className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation - scrollable */}
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {availableNavItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                if (item.isAction && item.id === 'onboarding') {
                  setShowOnboarding(true);
                } else {
                  setCurrentTab(item.id);
                }
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                item.highlight
                  ? 'bg-gradient-to-r from-[#ff4d4d]/20 to-orange-500/20 text-[#ff4d4d] border border-[#ff4d4d]/30 hover:from-[#ff4d4d]/30 hover:to-orange-500/30'
                  : currentTab === item.id
                  ? 'bg-[#ff4d4d]/20 text-[#ff4d4d]'
                  : 'text-white/60 hover:bg-white/5 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {isSidebarOpen && <span className="font-medium">{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* Subscription Badge */}
        {isSidebarOpen && (
          <div className="px-4 py-2 border-t border-white/10">
            <div className="px-3 py-2 bg-[#ff4d4d]/10 rounded-lg">
              <p className="text-xs text-white/50">{t.plan}</p>
              <p className="text-sm font-semibold text-[#ff4d4d]">{getSubscriptionTier()}</p>
            </div>
          </div>
        )}

        {isSidebarOpen && (
          <div className="px-4 py-2 border-t border-white/10">
            <div className="flex rounded-lg bg-white/5 p-1">
              <button
                type="button"
                onClick={() => setLanguage('vi')}
                className={`flex-1 rounded-md px-3 py-2 text-xs font-medium transition-colors ${language === 'vi' ? 'bg-[#ff4d4d]/20 text-[#ff4d4d]' : 'text-white/55 hover:text-white'}`}
              >
                {t.vietnamese}
              </button>
              <button
                type="button"
                onClick={() => setLanguage('en')}
                className={`flex-1 rounded-md px-3 py-2 text-xs font-medium transition-colors ${language === 'en' ? 'bg-[#ff4d4d]/20 text-[#ff4d4d]' : 'text-white/55 hover:text-white'}`}
              >
                {t.english}
              </button>
            </div>
          </div>
        )}

        {/* User Menu */}
        <div className="p-2 border-t border-white/10">
          <div className="relative">
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors ${
                isSidebarOpen ? '' : 'justify-center'
              }`}
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#ff4d4d] to-[#ff6b6b] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {getUserName().charAt(0).toUpperCase()}
              </div>
              {isSidebarOpen && (
                <>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-white truncate">{getUserName()}</p>
                    <p className="text-xs text-white/50 truncate">{getUserEmail()}</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </>
              )}
            </button>

            {isUserMenuOpen && (
              <div className={`absolute bottom-full mb-2 bg-[#0d1424] border border-white/10 rounded-xl shadow-xl overflow-hidden ${
                isSidebarOpen ? 'left-0 right-0' : 'left-0 w-48'
              }`}>
                <div className="p-3 border-b border-white/10">
                  <p className="text-sm font-semibold text-white truncate">{getUserName()}</p>
                  <p className="text-xs text-white/50 truncate">{getUserEmail()}</p>
                </div>
                <div className="py-1">
                  <button
                    onClick={() => setIsUserMenuOpen(false)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-white/70 hover:bg-white/5 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    {t.settings}
                  </button>
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      onLogout?.();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-white/70 hover:bg-white/5 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    {t.logout}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`flex-1 transition-all duration-300 ${
          isSidebarOpen ? 'ml-64' : 'ml-16'
        }`}
      >
        <div className="h-screen p-4 lg:p-6">
          {currentTab === 'claw9-chat' && featureFlags.hasClaw9Chat && (
            <Claw9Chat
              userEmail={getUserEmail()}
              initialChannel={typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('channel') || 'all' : 'all'}
              onOpenMessagingChannels={() => handleNavigate('messaging-channels')}
            />
          )}

          {currentTab === 'claw9-crm' && featureFlags.hasClaw9CRM && (
            <Claw9CRM
              userEmail={getUserEmail()}
              onNavigate={handleNavigate}
              onOpenMessagingChannels={() => handleNavigate('messaging-channels')}
            />
          )}

          {currentTab === 'claw9-agents' && featureFlags.hasClaw9Agents && (
            <Claw9Agents userEmail={getUserEmail()} />
          )}

          {currentTab === 'faq-self-improvement' && featureFlags.hasClaw9Agents && (
            <Claw9FaqSelfImprovement />
          )}

          {currentTab === 'messaging-channels' && featureFlags.hasClaw9Chat && (
            <MessengerSetupPanel
              onOpenInbox={(channel) => handleNavigate('claw9-chat', { channel })}
            />
          )}

          {currentTab === 'facebook-posts' && featureFlags.hasClaw9Chat && (
            <FacebookPostsDashboard />
          )}

          {currentTab === 'analytics' && featureFlags.hasClaw9Chat && (
            <Claw9Analytics />
          )}

          {currentTab === 'my-office' && (
            <MyOffice userEmail={getUserEmail()} />
          )}

          {currentTab === 'my-docs' && featureFlags.hasClaw9Chat && (
            <MyDocs userEmail={getUserEmail()} />
          )}

          {currentTab === 'notes' && featureFlags.hasClaw9Chat && (
            <NotesPage userEmail={getUserEmail()} />
          )}

          {currentTab === 'inventory' && featureFlags.hasClaw9Chat && (
            <InventoryPage userEmail={getUserEmail()} />
          )}

          {currentTab === 'deal-flow' && featureFlags.hasClaw9Agents && (
            <DealFlowPage userEmail={getUserEmail()} />
          )}

          {currentTab === 'google-integration' && (
            <GoogleIntegrations userEmail={getUserEmail()} />
          )}

          {currentTab === 'infrastructure' && (
            <InfrastructureDashboard />
          )}

          {/* Fallback if feature is not available */}
          {(currentTab === 'claw9-chat' && !featureFlags.hasClaw9Chat) ||
           (currentTab === 'claw9-crm' && !featureFlags.hasClaw9CRM) ||
           (currentTab === 'claw9-agents' && !featureFlags.hasClaw9Agents) ||
           (currentTab === 'faq-self-improvement' && !featureFlags.hasClaw9Agents) ||
           (currentTab === 'my-docs' && !featureFlags.hasClaw9Chat) ||
           (currentTab === 'notes' && !featureFlags.hasClaw9Chat) ||
           (currentTab === 'inventory' && !featureFlags.hasClaw9Chat) ||
           (currentTab === 'deal-flow' && !featureFlags.hasClaw9Agents) ||
           (currentTab === 'messaging-channels' && !featureFlags.hasClaw9Chat) ? (
            <div className="flex flex-col items-center justify-center h-full text-white/50">
              <div className="text-6xl mb-4">🔒</div>
              <h2 className="text-xl font-semibold mb-2">{t.featureLocked}</h2>
              <p className="text-center max-w-md">
                {t.featureLockedDescription}
              </p>
            </div>
          ) : null}
        </div>
      </main>

      {/* Onboarding Wizard Modal */}
      {showOnboarding && (
        <Claw9Onboarding
          language={language}
          onClose={() => setShowOnboarding(false)}
          onComplete={() => {
            setShowOnboarding(false);
            // Optionally navigate to a specific tab after completion
            handleNavigate('claw9-agents');
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
